import { useContext, useEffect, useState } from 'react';

import {
  DataServiceContext,
  useEditorStatusContext,
  useInstrumentationServiceContext,
  SiteConfigContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  useFloatingBannerMessage,
  filterImportedImages,
  disableImportedImages,
  ImageSource,
  getUniqueFacebookPhotos,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  facebookPageInfoFilter,
  importFromFacebook,
  hasFBOAuthError,
} from '@bingads-webui-campaign-react/smart-page-facebook-import-dialog';

export const useFacebookPhotos = (
  imagesToFilter = [],
  shouldStart = true,
  // If this is set to true, photo won't be filtered out from list, but give them a tag
  disableFilteredItem = false
) => {
  const [isFbSupported, setIsFbSupported] = useState(true);
  const [fbPhotos, setFbPhotos] = useState([]);
  const [filteredFbPhotos, setFilteredFbPhotos] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);

  const i18n = useContext(I18nContext);
  const { smartPageService, hostSystemScenario } = useContext(DataServiceContext);
  const { siteConfig } = useContext(SiteConfigContext);

  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const logger = useInstrumentationServiceContext();

  useEffect(() => {
    if (shouldStart) {
      importFromFacebook(
        siteConfig,
        smartPageService,
        facebookPageInfoFilter.photos,
        hostSystemScenario
      )
        .then(({ photos }) => {
          const uniquePhotos = getUniqueFacebookPhotos(photos);
          setFbPhotos(uniquePhotos);
          setIsFbSupported(true);
        })
        .catch((err) => {
          if (err && (err.message === 'NO_FB_AUTH' || hasFBOAuthError(err))) {
            if (hasFBOAuthError(err)) {
              logger.logTrace('Encounter Facebook OAuth error while importing FB photos');
            }
            setIsFbSupported(false);
          } else {
            setIsFbSupported(true);
            logger.logError(`Import photos from facebook failed. ${err}, error string: ${JSON.stringify(err)}`);
            showWarning(i18n.getString(_TL_('We were not able to find photos from this Facebook page. Please try again or reimport your Facebook Page.')));
          }
        })
        .finally(() => setIsInitializing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStart]);

  useEffect(() => {
    if (disableFilteredItem) {
      setFilteredFbPhotos(disableImportedImages(fbPhotos, imagesToFilter, ImageSource.Facebook));
    } else {
      setFilteredFbPhotos(filterImportedImages(fbPhotos, imagesToFilter, ImageSource.Facebook));
    }
  }, [fbPhotos, imagesToFilter, disableFilteredItem]);

  return {
    isFbSupported,
    fbPhotos: filteredFbPhotos,
    isInitializing,
  };
};
